// RequestAccessModal — the redesign's `.ra-modal` (bulk request wizard).
// design ref: styles.css:2467–2960, project/bulk-request.jsx:78–371
//
// Single-page modal (no wizard steps):
//   • Header: title + close ×
//   • Recipient bar: chip(s) + "+" recipient picker popover (Myself + every
//     person the manager can request on behalf of via /access/recipients)
//   • Main column: "Browse the catalog" + search/filter row + card grid
//   • Right drawer (appears once basket has ≥1 item): per-recipient table +
//     bulk duration picker + customise-per-item toggle
//   • Footer: Cancel / Send Request
//   • After a submit that made drafts (a policy wants the requester's passkey):
//     the signing panel over the basket signs each in turn, in place
//     (useDraftSigning). Closing the modal while it is up leaves the rest
//     unsigned in My Access.
//
// State lives in three focused hooks (useRecipientSelection / useCatalogBrowse
// / useBasket); this component is composition + the submit orchestration.

import {
  Box,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalContent,
  ModalOverlay,
  useBreakpointValue,
  useMediaQuery,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useRef } from "react";
import { TbX } from "react-icons/tb";
import { members } from "state/members/member";
import {
  MF_ACCESS,
  type EligibleRecipients,
  type MFAccessCatalog,
} from "state/members/access_v2/catalogs";
import { activeAccessResourceIds } from "state/members/access_v2/catalogAccess";
import type { MFMember } from "state/members/member";
import PloyButton from "components/ds/PloyButton";
import BasketDrawer from "./BasketDrawer";
import RecipientBar from "./RecipientBar";
import CatalogBrowser from "./CatalogBrowser";
import { useRecipientSelection } from "./useRecipientSelection";
import { useCatalogBrowse } from "./useCatalogBrowse";
import { useBasket } from "./useBasket";
import { useIdentitySelection } from "./useIdentitySelection";
import { unsendableForSelf } from "./identityOptions";
import { useSubmitRequest } from "./useSubmitRequest";
import { buildBulkPayloads, type SubmitRequestArgs } from "./payloads";
import MobileRequestFlow from "./mobile/MobileRequestFlow";
import MobileSheet from "./mobile/MobileSheet";
import { afterSigning, submitFollowUp } from "./submitFollowUp";
import { useInert } from "./useInert";
import DraftSigningPanel, {
  DRAFT_SIGNING_TITLE,
} from "pages/Members/StepUp/DraftSigningPanel";
import { useDraftSigning } from "pages/Members/StepUp/useDraftSigning";

export interface RequestAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Preselect a catalog (e.g. opened from a Catalog page). */
  initialCatalogId?: string | null;
  /** Preselect a catalog item / app (narrows the grid to one parent app). */
  initialCatalogItemId?: string | null;
  /** Seed the basket with these FlatRequestItem ids. */
  initialBasket?: string[];
  /** Visual-only override: skip the hooks and render with injected data
   *  (used by the design-system preview, which has no backend). */
  __previewData?: {
    me: MFMember;
    catalogs: MFAccessCatalog[];
    recipients?: EligibleRecipients;
  };
}

const EMPTY_RECIPIENTS: EligibleRecipients = { people: [], groups: [] };

const RequestAccessModal = ({
  isOpen,
  onClose,
  initialCatalogId = null,
  initialCatalogItemId = null,
  initialBasket = [],
  __previewData,
}: RequestAccessModalProps) => {
  // Always call the data hooks (Rules of Hooks); in preview mode the live
  // values are discarded in favour of the injected `__previewData`.
  const liveMe = members.useGetMe();
  const liveCatalogs = MF_ACCESS.useListAccessCatalogs();
  const liveRecipients = MF_ACCESS.useListEligibleRecipients();
  // Access the member already holds — badges the browse cards with
  // "You have access" so held resources aren't re-requested by mistake.
  const currentAccess = MF_ACCESS.useListAllCurrentAccess();
  const activeResourceIds = useMemo(
    () => activeAccessResourceIds(currentAccess),
    [currentAccess],
  );
  const me = __previewData?.me ?? liveMe;
  const catalogs = __previewData?.catalogs ?? liveCatalogs;
  const eligible =
    __previewData?.recipients ?? liveRecipients ?? EMPTY_RECIPIENTS;
  const toast = useToast();
  // Mobile gets the design's dedicated bottom-sheet flow (browse → configure);
  // desktop keeps the side-by-side modal. Same hooks feed both.
  const isMobile = useBreakpointValue({ base: true, md: false });
  // Below xl (or on short viewports ≤700px) the drawer doesn't have room for
  // the always-open duration config under the list — the config condenses to
  // a docked summary strip that slides up into a full card on hover/focus.
  const belowXl = useBreakpointValue({ base: true, xl: false }) ?? false;
  const [shortViewport] = useMediaQuery("(max-height: 700px)");
  const condensedConfig = belowXl || shortViewport;

  const selection = useRecipientSelection(me, eligible);
  // When opened focused on a catalog item we pin that item at the top and let
  // the "Add more resources" grid browse EVERY catalog — so the browse filters
  // start cleared rather than seeded to the originating catalog/item.
  const focused = Boolean(initialCatalogItemId);
  const catalog = useCatalogBrowse(catalogs, {
    catalogId: focused ? null : initialCatalogId,
    catalogItemId: null,
  });
  const basket = useBasket(
    catalog.flat,
    selection.resolvedRecipients,
    me?.id,
    initialBasket,
    // Focused on a catalog item → pre-add it if it's a single-resource item.
    focused ? initialCatalogItemId : null,
  );
  // Per-resource account choice for the requester's own rows (PLO-4704).
  const identity = useIdentitySelection(
    me,
    basket.basketItems,
    basket.roleSelection,
    selection.resolvedRecipients.some((r) => r.id === me?.id),
  );

  const { submit, isSubmitting } = useSubmitRequest();
  // A policy that wants the requester's passkey creates a draft instead,
  // signed in the panel over the basket.
  const draftSigning = useDraftSigning();
  const signingDrafts = draftSigning.active;
  // Set when the modal itself is closed mid-signing, so the run's end closes it.
  const closeWhenStopped = useRef(false);
  const browseRef = useInert<HTMLDivElement>(signingDrafts);

  // Dropped for the SELF recipient only — other recipients still get the
  // resource with the backend default. Per-account duration over-max is likewise
  // excluded per account inside buildBulkPayloads, so a valid recipient/account
  // row is never lost to an invalid sibling.
  const identityIncompleteItems = unsendableForSelf(
    basket.basketItems,
    identity,
  );

  const submitArgs: SubmitRequestArgs = {
    recipients: selection.resolvedRecipients,
    items: basket.submittableItems,
    bulkDuration: basket.bulkDuration,
    bulkReason: basket.bulkReason,
    perItem: basket.perItem,
    perItemCfg: basket.perItemCfg,
    perAccountDuration: basket.perAccountDuration,
    perRoleDuration: basket.perRoleDuration,
    reasonOnlyRequired: basket.reasonOnlyRequired,
    roleSelection: basket.roleSelection,
    itemView: basket.itemView,
    identityByItem: identity.selection,
    selfMemberId: me?.id,
    identityIncompleteItems,
  };

  // The ACTUAL requests the fan-out will make (recipients × chosen accounts ×
  // resources, minus per-recipient/account exclusions). Drives both the submit
  // gate — a resource can be resource-level valid yet contribute nothing once
  // exclusions apply (e.g. the only recipient is self with an empty/over-max
  // account) — and the button's count, so the label matches what's sent.
  const requestCount = buildBulkPayloads(submitArgs).length;
  const optionsHint = basket.recipientOptions.error
    ? basket.recipientOptions.error
    : basket.recipientOptions.loading
      ? "Checking what each person can request…"
      : identity.loading
        ? "Checking which of your accounts can be used…"
        : null;
  const canSubmit =
    requestCount > 0 &&
    selection.recipients.length > 0 &&
    !isSubmitting &&
    !signingDrafts &&
    !basket.hasMissingRequiredReason &&
    !optionsHint;

  // Every way out of the modal (close, Escape, Cancel). Mid-signing it stops
  // the run instead, which says what is left unsigned and then closes.
  const closeModal = () => {
    closeWhenStopped.current = true;
    if (draftSigning.stop()) return;
    closeWhenStopped.current = false;
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const next = submitFollowUp(await submit(submitArgs));
      if (next.toast) toast(next.toast);
      if (next.sign) {
        const { draftIds, closeAfter } = next.sign;
        draftSigning.start(draftIds, (result) => {
          const after = afterSigning(
            result,
            closeAfter || closeWhenStopped.current,
          );
          closeWhenStopped.current = false;
          if (after.toast) toast(after.toast);
          if (after.close) onClose();
        });
      } else if (next.close) {
        onClose();
      }
    } catch {
      toast({
        status: "error",
        title: "An error occurred submitting your request",
        variant: "subtle",
      });
    }
  };

  const signingPanel = signingDrafts ? (
    <DraftSigningPanel signing={draftSigning} />
  ) : undefined;

  // Mobile: the design's bottom-sheet flow instead of the desktop modal. The
  // signing panel takes the configure sheet's place while it is up.
  if (isMobile) {
    if (!isOpen) return null;
    if (signingDrafts) {
      return (
        <MobileSheet isOpen onClose={closeModal} label={DRAFT_SIGNING_TITLE}>
          <Box py="1xl">{signingPanel}</Box>
        </MobileSheet>
      );
    }
    return (
      <MobileRequestFlow
        onClose={closeModal}
        catalog={catalog}
        basket={basket}
        selection={selection}
        identity={identity}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        canSubmit={canSubmit}
        submitHint={optionsHint}
        activeResourceIds={activeResourceIds}
      />
    );
  }

  // The signing panel lives over the basket, so the drawer stays while it is up.
  const drawerOpen = basket.basketItems.length > 0 || signingDrafts;

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      isCentered
      size="full"
      autoFocus={false}
      returnFocusOnClose={false}
    >
      <ModalOverlay bg="rgba(0,0,0,0.45)" />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        m={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        maxH="100vh"
        w="100vw"
      >
        {/* .ra-modal — 90vw / 90vh, max 1600/960, 16px radius */}
        <Box
          w="90vw"
          maxW="1600px"
          h="90vh"
          maxH="960px"
          bg="neutral.0"
          borderRadius="component.4xl" // 16px
          display="flex"
          flexDirection="column"
          overflow="hidden"
          boxShadow="wizard"
        >
          {/* .ra-modal__header */}
          <Flex
            align="center"
            gap="lg"
            pl="28px"
            pr="28px"
            pt="22px"
            pb="18px"
            borderBottomWidth="1px"
            borderBottomStyle="solid"
            borderBottomColor="neutral.100"
          >
            <Box
              fontFamily="heading"
              fontWeight={500}
              fontSize="2xl1" // 26px
              color="neutral.950"
              letterSpacing="-0.01em"
              lineHeight="1.1"
              sx={{ textWrap: "balance" }}
            >
              Request access
            </Box>
            <Box
              as="button"
              type="button"
              aria-label="Close"
              onClick={closeModal}
              ml="auto"
              w="32px"
              h="32px"
              borderRadius="component.lg"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              color="neutral.700"
              bg="transparent"
              border="none"
              cursor="pointer"
              transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
              _hover={{ bg: "neutral.100", color: "neutral.950" }}
              _active={{ transform: "scale(0.96)" }}
            >
              <Icon as={TbX} boxSize="18px" />
            </Box>
          </Flex>

          {/* .ra-modal__body — main column + (optional) right drawer */}
          <Flex flex="1" overflow="hidden" minH={0}>
            {/* .ra-modal__main: paused while drafts are signed. */}
            <Box
              ref={browseRef}
              flex="3"
              display="flex"
              flexDirection="column"
              overflow="hidden"
              minW={0}
              opacity={signingDrafts ? 0.55 : 1}
              transition="opacity 160ms cubic-bezier(0.23, 1, 0.32, 1)"
            >
              <RecipientBar selection={selection} />
              <CatalogBrowser
                catalog={catalog}
                basket={basket}
                focusedCatalogItemId={initialCatalogItemId}
                activeResourceIds={activeResourceIds}
              />
            </Box>

            {drawerOpen && (
              <BasketDrawer
                condensed={condensedConfig}
                recipients={selection.resolvedRecipients}
                items={basket.basketItems}
                bulkDuration={basket.bulkDuration}
                onBulkDurationChange={basket.setBulkDuration}
                bulkReason={basket.bulkReason}
                onBulkReasonChange={basket.setBulkReason}
                perItem={basket.perItem}
                onPerItemChange={basket.setPerItemMode}
                perItemCfg={basket.perItemCfg}
                onPerItemCfgPatch={basket.patchPerItem}
                perAccountDuration={basket.perAccountDuration}
                onPerAccountDurationPatch={basket.patchAccountDuration}
                perRoleDuration={basket.perRoleDuration}
                onPerRoleDurationPatch={basket.patchRoleDuration}
                onRemoveItem={basket.toggle}
                rolesFor={basket.rolesFor}
                onToggleRole={basket.toggleRole}
                recipientItem={basket.recipientItem}
                sharesRoles={basket.sharesRoles}
                basketTerms={basket.basketTerms}
                rowStatus={basket.rowStatus}
                reasonRequired={basket.anyReasonRequired}
                reasonRequiredCount={basket.reasonRequiredCount}
                submittableCount={basket.submittableItems.length}
                reasonOnlyRequired={basket.reasonOnlyRequired}
                onReasonOnlyRequiredChange={basket.setReasonOnlyRequired}
                identity={identity}
                overlay={signingPanel}
              />
            )}
          </Flex>

          {/* .ra-modal__footer */}
          <HStack
            justify="flex-end"
            spacing="sm"
            px="28px"
            py="lg"
            borderTopWidth="1px"
            borderTopStyle="solid"
            borderTopColor="neutral.100"
          >
            {optionsHint && (
              <HStack mr="auto" spacing="sm">
                <Box
                  fontSize="smd"
                  color={
                    basket.recipientOptions.error ? "error.700" : "neutral.600"
                  }
                  role={basket.recipientOptions.error ? "alert" : "status"}
                >
                  {optionsHint}
                </Box>
                {basket.recipientOptions.retry && (
                  <PloyButton
                    variant="ghost"
                    size="sm"
                    onClick={basket.recipientOptions.retry}
                  >
                    Try again
                  </PloyButton>
                )}
              </HStack>
            )}
            {/* .ra-cancel — azurite-coloured text button. Ghost PloyButton +
                colour overrides: padded chrome, brand p.600/p.50 palette. */}
            <PloyButton
              variant="ghost"
              size="md"
              onClick={closeModal}
              color="p.600"
              _hover={{ color: "p.700", bg: "p.50" }}
            >
              Cancel
            </PloyButton>
            <PloyButton
              variant="primary"
              size="md"
              onClick={handleSubmit}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            >
              {requestCount > 1 ? `Send ${requestCount} Requests` : "Send Request"}
            </PloyButton>
          </HStack>
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default RequestAccessModal;
